// =============================================
// FORMULARIO DE GASTOS
// Crear y editar gastos con upload de comprobante
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import expenseService from '../../services/expenseService';
import expenseCategoryService from '../../services/expenseCategoryService';

const ExpenseForm = ({ open, onClose, expense, onSuccess }) => {
  // GASTOS: Estados del componente
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  // GASTOS: Estado del formulario
  const [formData, setFormData] = useState({
    category_id: '',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    notes: '',
    receipt: null,
  });

  // GASTOS: Cargar categorías y datos del gasto
  useEffect(() => {
    loadCategories();
    
    if (expense) {
      // Modo edición
      setFormData({
        category_id: expense.category_id || '',
        description: expense.description || '',
        amount: expense.amount || '',
        expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
        notes: expense.notes || '',
        receipt: null, // No cargar el archivo existente
      });
      
      // Mostrar imagen existente
      if (expense.receipt_path) {
        setImagePreview(expenseService.getReceiptUrl(expense.receipt_path));
      }
    } else {
      // Modo creación
      resetForm();
    }
  }, [expense, open]);

  // GASTOS: Cargar categorías activas
const loadCategories = async () => {
  try {
    const response = await expenseService.getCategories();
    setCategories(response.categories || []);
  } catch (err) {
    console.error('Error al cargar categorías:', err);
  }
};

  // GASTOS: Resetear formulario
  const resetForm = () => {
    setFormData({
      category_id: '',
      description: '',
      amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      notes: '',
      receipt: null,
    });
    setImagePreview(null);
    setError('');
  };

  // GASTOS: Manejar cambios en campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setError('');
  };

  // GASTOS: Manejar selección de archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validar tipo de archivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes (JPG, PNG, GIF, WebP)');
        return;
      }

      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no debe superar 5MB');
        return;
      }

      // Guardar archivo y crear preview
      setFormData({ ...formData, receipt: file });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setError('');
    }
  };

  // GASTOS: Eliminar imagen seleccionada
  const handleRemoveImage = () => {
    setFormData({ ...formData, receipt: null });
    setImagePreview(expense?.receipt_path ? expenseService.getReceiptUrl(expense.receipt_path) : null);
    
    // Limpiar input file
    const fileInput = document.getElementById('receipt-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // GASTOS: Validar y enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validaciones
      if (!formData.category_id) {
        setError('La categoría es requerida');
        setLoading(false);
        return;
      }

      if (!formData.description.trim()) {
        setError('La descripción es requerida');
        setLoading(false);
        return;
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        setError('El monto debe ser mayor a 0');
        setLoading(false);
        return;
      }

      if (!formData.expense_date) {
        setError('La fecha es requerida');
        setLoading(false);
        return;
      }

      // En modo creación, el comprobante es obligatorio
      if (!expense && !formData.receipt) {
        setError('El comprobante es requerido');
        setLoading(false);
        return;
      }

      // Preparar datos
      // Forzar fecha local sin conversión de zona horaria
      const localDate = new Date(formData.expense_date + 'T00:00:00');
      const formattedDate = localDate.toISOString().split('T')[0];
      
      const expenseData = {
        category_id: formData.category_id,
        description: formData.description.trim(),
        amount: parseFloat(formData.amount),
        expense_date: formattedDate,
        notes: formData.notes.trim() || null,
        receipt: formData.receipt,
      };

      // Crear o actualizar
      if (expense) {
        await expenseService.update(expense.id, expenseData);
      } else {
        await expenseService.create(expenseData);
      }

      onSuccess();
      onClose();
      resetForm();

    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error al guardar gasto';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {expense ? 'Editar Gasto' : 'Registrar Gasto'}
        </DialogTitle>

        <DialogContent>
          {/* GASTOS: Mostrar errores */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* GASTOS: Categoría */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Categoría"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <MenuItem value="">Seleccionar categoría</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* GASTOS: Monto */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                required
                disabled={loading}
                inputProps={{ step: '0.01', min: '0' }}
                helperText="Ingrese el monto en USD"
              />
            </Grid>

            {/* GASTOS: Fecha */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha del Gasto"
                name="expense_date"
                type="date"
                value={formData.expense_date}
                onChange={handleChange}
                required
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* GASTOS: Descripción */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                disabled={loading}
                multiline
                rows={2}
                helperText="Describe el gasto realizado"
              />
            </Grid>

            {/* GASTOS: Notas */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas Adicionales"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={loading}
                multiline
                rows={2}
              />
            </Grid>

            {/* GASTOS: Upload de comprobante */}
            <Grid item xs={12}>
              <Box sx={{ border: '1px dashed grey', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Comprobante {!expense && '(Requerido)'}
                </Typography>
                
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="receipt-upload"
                  type="file"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                
                <label htmlFor="receipt-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={loading}
                  >
                    {imagePreview ? 'Cambiar Imagen' : 'Subir Imagen'}
                  </Button>
                </label>

                {/* GASTOS: Preview de imagen */}
                {imagePreview && (
                  <Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        bgcolor: 'background.paper',
                      }}
                      onClick={handleRemoveImage}
                      disabled={loading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Formatos: JPG, PNG, GIF, WebP (máx. 5MB)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading} size="large">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : expense ? 'Actualizar' : 'Registrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ExpenseForm;
